import { z } from 'zod'

// =====================
// Ticket SLA lifecycle + auto-assignment config
// =====================
//
// SLA model: a ticket type with hasSlaTimer defines a resolution target
// (slaTargetMinutes) and optionally a first-response target
// (slaFirstResponseMinutes). At creation the ticket gets slaDueAt /
// slaFirstResponseDueAt. The clock PAUSES while the ticket sits in a
// WAITING_ON_REQUESTER stage (slaPausedAt set; on resume both due-ats shift
// forward by the paused duration, accumulated in slaTotalPausedMinutes).
// An escalation cron warns at warnAtPercent (default 80) and escalates at
// breach; per-timer escalation levels on the ticket dedupe notifications
// (0 = none, 1 = warned, 2 = breached).

// Optional business-hours calendar: the SLA clock only runs during these
// hours. days uses 0 (Sunday) – 6 (Saturday); hours are 0–23 in timeZone.
export const SlaBusinessHoursSchema = z.object({
  timeZone: z.string().min(1),
  days: z.array(z.number().int().min(0).max(6)).min(1),
  startHour: z.number().int().min(0).max(23),
  endHour: z.number().int().min(1).max(24),
})

export type SlaBusinessHours = z.infer<typeof SlaBusinessHoursSchema>

// Stored in TicketTypeDef.slaConfig (Json).
export const TicketSlaConfigSchema = z.object({
  // Percentage of the target at which the "at risk" warning fires.
  warnAtPercent: z.number().int().min(1).max(99).optional(),
  // Users notified at warn/breach in addition to the ticket's assignees.
  escalateUserIds: z.array(z.string().uuid()).max(20).optional(),
  // Optional reassign target when the resolution timer breaches.
  reassignOnBreachUserId: z.string().uuid().optional(),
  businessHours: SlaBusinessHoursSchema.optional(),
})

export type TicketSlaConfig = z.infer<typeof TicketSlaConfigSchema>

export const SLA_DEFAULT_WARN_AT_PERCENT = 80

// Escalation dedupe levels stored on the ticket, per timer.
export enum SlaEscalationLevel {
  NONE = 0,
  WARNED = 1,
  BREACHED = 2,
}

// Computed display status for a ticket's SLA chip.
export enum SlaDisplayStatus {
  PAUSED = 'PAUSED',
  ON_TRACK = 'ON_TRACK',
  AT_RISK = 'AT_RISK',
  BREACHED = 'BREACHED',
  MET = 'MET',
}

// Semantic identifiers for the SLA lifecycle notifications, carried in the
// notification payload's data.slaEvent / data.slaTimer. NotificationType is a
// closed Postgres enum (adding values needs a migration), so SLA
// notifications are persisted as TICKET_UPDATED rows and distinguished by
// these markers; switch the stored type over once the enum values land.
export const TICKET_SLA_NOTIFICATION_EVENTS = {
  AT_RISK: 'TICKET_SLA_AT_RISK',
  BREACHED: 'TICKET_SLA_BREACHED',
} as const

export type TicketSlaNotificationEvent =
  (typeof TICKET_SLA_NOTIFICATION_EVENTS)[keyof typeof TICKET_SLA_NOTIFICATION_EVENTS]

// =====================
// Auto-assignment
// =====================
//
// Applied at ticket creation. On OrganizationInbox the pool is
// defaultAssigneeIds and ALL preserves the legacy assign-everyone behavior;
// on TicketTypeDef the pool is assigneePoolIds and NONE preserves the legacy
// single defaultAssigneeId behavior. LOAD_BALANCED picks the pool member
// with the fewest open tickets; assignmentMaxOpenTickets skips members
// at/over the cap (falling back to least-loaded when everyone is over).
export enum TicketAssignmentStrategyEnum {
  NONE = 'NONE',
  ALL = 'ALL',
  ROUND_ROBIN = 'ROUND_ROBIN',
  LOAD_BALANCED = 'LOAD_BALANCED',
}
